import { ApolloError, useMutation, useQuery } from '@apollo/client';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Card,
  Container,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
} from '@mui/material';
import { Table } from 'components/common/table';
import { CreateTestLeadButtonDialog } from 'components/views/campaign/test-leads/create-test-lead-button-dialog';
import {
  CampaignTestLeadsAdvanceFlowMutation,
  CampaignTestLeadsQuery,
  CampaignTestLeadsResetLeadMutation,
} from 'components/views/campaign/test-leads/test-leads.graphql';
import { useNavTableConnection } from 'hooks/use-nav-table-connection';
import { useSnackbar } from 'providers/snackbar';
import { FC, MouseEvent, useCallback, useState } from 'react';
import { useParams } from 'react-router';
import {
  CampaignTestLeads as ICampaignTestLeads,
  CampaignTestLeads_campaign_testLeads,
  CampaignTestLeadsVariables,
} from 'types/apollo/CampaignTestLeads';
import {
  CampaignTestLeadsAdvanceFlow,
  CampaignTestLeadsAdvanceFlowVariables,
} from 'types/apollo/CampaignTestLeadsAdvanceFlow';
import {
  CampaignTestLeadsResetLead,
  CampaignTestLeadsResetLeadVariables,
} from 'types/apollo/CampaignTestLeadsResetLead';
import { vendorIdToName } from 'utils/vendor';

function useResetLeadMutation() {
  const { openSnackbar } = useSnackbar();

  const [resetLeadMutation] = useMutation<
    CampaignTestLeadsResetLead,
    CampaignTestLeadsResetLeadVariables
  >(CampaignTestLeadsResetLeadMutation);

  return useCallback(
    async (leadId: string, internalCampaignId: string) => {
      try {
        await resetLeadMutation({
          variables: {
            leadResetData: { campaignId: internalCampaignId, leadId },
          },
        });
        openSnackbar({
          message: 'Lead reset successfully. Lead status will update after a short delay.',
          severity: 'success',
        });
      } catch (err) {
        if (
          err instanceof ApolloError &&
          err.message?.includes('status code 412') /* PreconditionFailedException */
        ) {
          openSnackbar({
            message: 'Lead has not been processed yet. Please wait and try again later.',
            severity: 'error',
          });
        }
      }
    },
    [openSnackbar],
  );
}

function useAdvanceFlowMutation() {
  const { openSnackbar } = useSnackbar();

  const [advanceFlowMutation] = useMutation<
    CampaignTestLeadsAdvanceFlow,
    CampaignTestLeadsAdvanceFlowVariables
  >(CampaignTestLeadsAdvanceFlowMutation);

  return useCallback(
    async (leadId: string, internalCampaignId: string) => {
      try {
        await advanceFlowMutation({
          variables: {
            leadAdvanceFlowData: { campaignId: internalCampaignId, leadId },
          },
        });
        openSnackbar({ message: 'Lead flow advanced successfully', severity: 'success' });
      } catch (err) {
        console.log('err', err);
        if (
          err instanceof ApolloError &&
          err.message?.includes('status code 412') /* PreconditionFailedException */
        ) {
          openSnackbar({
            message: 'Lead has not been processed yet. Please wait and try again later.',
            severity: 'error',
          });
        } else {
          openSnackbar({ message: 'Lead flow could not be advanced', severity: 'error' });
        }
      }
    },
    [openSnackbar],
  );
}

interface CampaignTestLeadOverflowMenuProps {
  leadId: string;
  internalCampaignId: string;
  resetLead: (leadId: string, internalCampaignId: string) => void;
  advanceLead: (leadId: string, internalCampaignId: string) => void;
}

const CampaignTestLeadOverflowMenu: FC<CampaignTestLeadOverflowMenuProps> = ({
  leadId,
  internalCampaignId,
  resetLead,
  advanceLead,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        id="row-overflow-button"
        aria-label="more options"
        aria-controls={menuOpen ? 'row-overflow-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={menuOpen ? 'true' : undefined}
        onClick={handleClick}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="row-overflow-menu"
        anchorEl={anchorEl}
        aria-labelledby="row-overflow-button"
        open={menuOpen}
        onClose={handleClose}
      >
        <MenuItem
          onClick={() => {
            handleClose();
            resetLead(leadId, internalCampaignId);
          }}
        >
          <ListItemText>Reset lead</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            advanceLead(leadId, internalCampaignId);
          }}
        >
          <ListItemText>Advance flow</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export const CampaignTestLeads: FC = () => {
  const { campaignId: id } = useParams();
  const resetLead = useResetLeadMutation();
  const advanceLead = useAdvanceFlowMutation();

  const { data, loading } = useQuery<ICampaignTestLeads, CampaignTestLeadsVariables>(
    CampaignTestLeadsQuery,
    {
      variables: { campaignId: id ?? '0' },
    },
  );

  const tableData = useNavTableConnection<CampaignTestLeads_campaign_testLeads>([
    {
      id: 'phone',
      label: 'Phone',
      sortable: false,
    },
    {
      id: 'firstName',
      label: 'First Name',
      sortable: false,
    },
    {
      id: 'source',
      label: 'Source',
      sortable: false,
    },
    {
      id: 'vendorId',
      label: 'Vendor',
      sortable: false,
      renderer: (row) => vendorIdToName(row.vendorId),
    },
    {
      id: 'isDnc',
      label: 'Status',
      sortable: false,
      renderer: (row) => (row.isDnc ? 'DNC' : 'Active'),
    },
    {
      id: 'id',
      sortable: false,
      numeric: true,
      renderer: (row) =>
        data?.campaign?.id && (
          <CampaignTestLeadOverflowMenu
            leadId={row.id}
            internalCampaignId={data.campaign.id}
            resetLead={resetLead}
            advanceLead={advanceLead}
          />
        ),
    },
  ]);

  const tableRows = data?.campaign?.testLeads || [];

  return (
    <Container
      maxWidth="xl"
      sx={{
        p: 3,
      }}
    >
      <Stack justifyContent="flex-start" alignItems="flex-start" spacing={2}>
        {data?.campaign?.companyId && (
          <CreateTestLeadButtonDialog
            companyId={data.campaign.companyId.toString()}
            internalCampaignId={data.campaign.id.toString()}
          ></CreateTestLeadButtonDialog>
        )}
        <Box flex={1} sx={{ width: '100%' }}>
          <Card variant="outlined">
            <Table loading={loading} rows={tableRows} {...tableData} />
          </Card>
        </Box>
      </Stack>
    </Container>
  );
};
